import React, { useState } from 'react';
import Layout from '@/components/Layout';
import BackToDashboard from '@/components/BackToDashboard';
import { useLang } from '@/context/LangContext';
import { PlayCircle, BookMarked, X as XIcon, ExternalLink } from 'lucide-react';

const LESSONS = [
  {
    id: 1, titleKey: 'academy_lesson_1_title', catKey: 'academy_cat_fundamentals',
    img: 'https://images.pexels.com/photos/6076502/pexels-photo-6076502.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600',
    duration: '8 min',
    query: 'basketball spacing 4 out 1 in tutorial coach',
  },
  {
    id: 2, titleKey: 'academy_lesson_2_title', catKey: 'academy_cat_vs_zone',
    img: 'https://images.unsplash.com/photo-1505666287802-931dc83948e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHxiYXNrZXRiYWxsJTIwZ2FtZSUyMGFjdGlvbnxlbnwwfHx8fDE3ODE0NDQwMDF8MA&ixlib=rb-4.1.0&q=85',
    duration: '12 min',
    query: 'how to attack 2-3 zone defense basketball',
  },
  {
    id: 3, titleKey: 'academy_lesson_3_title', catKey: 'academy_cat_defense',
    img: 'https://images.unsplash.com/photo-1504450758481-7338eba7524a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxuYmElMjBzdGFkaXVtfGVufDB8fHx8MTc4MTQ0NDAwMXww&ixlib=rb-4.1.0&q=85',
    duration: '15 min',
    query: 'pick and roll defense drop coverage vs switch NBA',
  },
  {
    id: 4, titleKey: 'academy_lesson_4_title', catKey: 'academy_cat_iq',
    img: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxiYXNrZXRiYWxsJTIwcGxheWVyfGVufDB8fHx8MTc4MTQ0NDAwMXww&ixlib=rb-4.1.0&q=85',
    duration: '10 min',
    query: 'basketball IQ reading the game pace tutorial',
  },
  {
    id: 5, titleKey: 'academy_lesson_5_title', catKey: 'academy_cat_systems',
    img: 'https://images.pexels.com/photos/6076502/pexels-photo-6076502.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600',
    duration: '14 min',
    query: 'horns offense action basketball tutorial',
  },
  {
    id: 6, titleKey: 'academy_lesson_6_title', catKey: 'academy_cat_ato_blob',
    img: 'https://images.unsplash.com/photo-1721750475007-b067a9e6112f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxiYXNrZXRiYWxsJTIwY29hY2glMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODE0NDQwMDF8MA&ixlib=rb-4.1.0&q=85',
    duration: '11 min',
    query: 'BLOB SLOB plays basketball end of game tutorial',
  },
];

function youtubeSearchUrl(query) {
  return `https://www.youtube.com/results?search_query=${encodeURIComponent(query)}`;
}

export default function Academy() {
  const { t } = useLang();
  const [selected, setSelected] = useState(null);

  return (
    <Layout>
      <BackToDashboard />
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">{t('nav_academy')}</div>
        <h1 className="font-h uppercase text-4xl sm:text-5xl flex items-center gap-3">
          <BookMarked className="w-9 h-9 text-gold" /> {t('academy_title')}
        </h1>
        <p className="text-gray-400 mt-2">{t('academy_subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {LESSONS.map(l => (
          <div
            key={l.id}
            data-testid={`academy-lesson-${l.id}`}
            onClick={() => setSelected(l)}
            className="bg-card-steel border border-gold/30 rounded-lg overflow-hidden hover-lift cursor-pointer"
          >
            <div className="relative aspect-video bg-navy-deep">
              <img src={l.img} alt={t(l.titleKey)} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/20 to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center group">
                <div className="w-16 h-16 rounded-full bg-gold/90 text-navy flex items-center justify-center group-hover:scale-110 transition">
                  <PlayCircle className="w-9 h-9" />
                </div>
              </div>
              <div className="absolute bottom-2 right-2 bg-navy-deep/90 border border-gold/40 rounded px-2 py-0.5 text-xs font-mono text-gold">
                {l.duration}
              </div>
            </div>
            <div className="p-4">
              <div className="text-xs font-mono uppercase tracking-widest text-gold mb-1">{t(l.catKey)}</div>
              <div className="font-h uppercase text-lg text-white">{t(l.titleKey)}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Lesson modal */}
      {selected && (
        <div
          data-testid="academy-modal"
          className="fixed inset-0 z-50 bg-navy-deep/95 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
          onClick={(e) => { if (e.target === e.currentTarget) setSelected(null); }}
        >
          <div className="w-full max-w-3xl bg-navy border-2 border-gold/60 rounded-lg p-5 relative">
            <button
              onClick={() => setSelected(null)}
              data-testid="academy-modal-close"
              className="absolute top-3 right-3 p-2 rounded-md border border-gold/40 text-gold hover:bg-gold/10"
              aria-label={t('close')}
            >
              <XIcon className="w-4 h-4" />
            </button>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-1">{t(selected.catKey)}</div>
            <h2 className="font-h uppercase text-2xl sm:text-3xl mb-4 pr-10">{t(selected.titleKey)}</h2>
            <div className="aspect-video rounded-md overflow-hidden border border-gold/30 mb-4 relative">
              <img src={selected.img} alt={t(selected.titleKey)} className="w-full h-full object-cover opacity-60" />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6">
                <PlayCircle className="w-16 h-16 text-gold" />
                <p className="text-sm text-white">{t('academy_will_open_youtube')}</p>
              </div>
            </div>
            <a
              href={youtubeSearchUrl(selected.query)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="academy-watch-on-youtube"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-gold text-navy font-h uppercase tracking-wider font-semibold hover:bg-gold-light transition"
            >
              <ExternalLink className="w-4 h-4" /> {t('academy_watch_youtube')}
            </a>
            <p className="text-xs text-gray-500 mt-3 text-center">{t('academy_coming_soon')}</p>
          </div>
        </div>
      )}
    </Layout>
  );
}
